<template>
   <pb-body-base :busy="loading">
      <template v-slot:default>
         <pb-body banner-size="sm"
                  :banner-img="fileResourceUrl(farbkarte.karte)"
                  :page-title="modalTitle">
            <template slot="body">
               <b-container>
                  <div v-if="error">
                     <b-alert variant="warning" show>{{ error }}</b-alert>
                  </div>
                  <div v-if="farbkarte && farbenWithFoto.length > 0" class="farbkarte-grid" :class="'bg-' + selectedBg">
                     <div class="bg-switcher mb-3">
                        <span class="bg-switcher-label">Background:</span>
                        <button v-for="bg in backgrounds" :key="bg.key"
                                class="bg-swatch"
                                :class="{ active: selectedBg === bg.key }"
                                :style="{ backgroundColor: bg.color }"
                                :title="bg.label"
                                @click="selectedBg = bg.key">
                        </button>
                     </div>
                     <b-row>
                        <div v-for="(color, index) in farbenWithFoto"
                             :key="index" class="col-md-4 col-lg-2dot4 article-link mb-2 card-deck">
                           <div class="color-card">
                              <pb-card :hoverable="false">
                                 <template slot="image">
                                    <img :src="imageResource(color.foto, 240)"
                                         alt="thumbnail"/>
                                 </template>
                                 <template slot="content">
                              <span
                                  class="d-block">{{ color.farbnummer }}</span>
                                 </template>
                              </pb-card>
                              <div v-if="hasOverlay(color)" class="color-card-overlay">
                                 <span v-if="color.certified"
                                       class="icon-oeko-tex color-card-oeko"
                                       title="OEKO-TEX certified"></span>
                                 <ul v-if="color.tags && color.tags.length > 0" class="color-card-tags">
                                    <li v-for="(tag, tagIndex) in color.tags" :key="tagIndex">{{ tag }}</li>
                                 </ul>
                              </div>
                           </div>
                        </div>
                     </b-row>
                  </div>
                  <b-alert v-else-if="farbkarte && farbenWithFoto.length === 0" variant="info" show>No color images available for this material.</b-alert>
               </b-container>
            </template>
         </pb-body>
      </template>
   </pb-body-base>
</template>

<script>
import ArticleApi from "@shared/_api/article";

export default {
   name: 'farbkarte-detail',
   data() {
      return {
         loading: false,
         farbkarte: null,
         error: null,
         selectedBg: 'light',
         backgrounds: [
            { key: 'light', label: 'Light Gray', color: '#f0f0f0' },
            { key: 'dark-gray', label: 'Dark Gray', color: '#616160' },
            { key: 'dark', label: 'Dark', color: '#333333' }
         ]
      }
   },
   computed: {
      farbenWithFoto() {
         if (this.farbkarte && this.farbkarte.farben) {
            return this.farbkarte.farben.filter(c => c.foto);
         }
         return [];
      },
      modalTitle() {
         if (this.farbkarte) {
            return `Color Card - ${this.farbkarte.qualitaetnummer}`;
         }
         return 'Color Card';
      }
   },
   mounted() {
      this.loadFarbkarte(this.$route.params.qualitaetId);
   },
   beforeRouteUpdate(to, from, next) {
      this.loadFarbkarte(to.params.qualitaetId);
      next();
   },
   methods: {
      loadFarbkarte(qualitaetId) {
         this.loading = true;
         this.farbkarte = null;
         this.error = null;

         ArticleApi.farbkarteByQualitaetId(qualitaetId)
             .then(data => {
                this.farbkarte = data;
             })
             .catch(() => {
                this.error = 'No color card found for this quality.';
             })
             .finally(() => {
                this.loading = false;
             });
      },
      hasOverlay(color) {
         return color.certified || (color.tags && color.tags.length > 0);
      },
      onImageError(event, color) {
         let el = event.target;
         let parent = el.parentElement;
         el.style.display = 'none';
         let fallback = document.createElement('div');
         fallback.className = 'farbkarte-color-number';
         fallback.textContent = color.farbnummer;
         parent.insertBefore(fallback, el);
      }
   }
}
</script>

<style lang="scss" scoped>
@import "~@shared/_assets/scss/constants";

.farbkarte-banner {
   max-height: 400px;
   object-fit: contain;
}

.bg-switcher {
   display: flex;
   align-items: center;
   gap: 8px;

   .bg-switcher-label {
      font-size: 13px;
      font-weight: 500;
      color: #666;
   }

   .bg-swatch {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 2px solid #ccc;
      cursor: pointer;
      padding: 0;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:hover {
         border-color: #888;
      }

      &.active {
         border-color: #333;
         box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
      }
   }
}

.color-card {
   position: relative;
   flex: 1 0 0%;
   display: flex;
   flex-direction: column;
   overflow: hidden;
   border-radius: 6px;
   transition: box-shadow 0.25s ease, transform 0.25s ease;

   &:hover {
      box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.3);
      transform: translateY(-2px);
   }

   .color-card-overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 16px;
      text-align: center;
      color: #ffffff;
      background: rgba(17, 17, 17, 0.5);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.25s ease, visibility 0.25s ease;
   }

   &:hover .color-card-overlay {
      opacity: 1;
      visibility: visible;
   }

   .color-card-oeko {
      margin-bottom: 12px;

      &:before {
         width: 64px;
         height: 64px;
      }
   }

   .color-card-tags {
      list-style: none;
      margin: 0;
      padding: 0;
      font-family: $font-robo;
      font-size: 13px;
      font-weight: 300;
      line-height: 1.5;

      li + li {
         margin-top: 4px;
      }
   }
}

.farbkarte-grid {
   padding: 20px;
   border-radius: 8px;
   transition: background-color 0.3s ease;

   &.bg-light {
      background-color: #f0f0f0;

      ::v-deep .pb-card .pb-card-img {
         background: #f0f0f0;
      }
   }

   &.bg-dark-gray {
      background-color: #616160;

      ::v-deep .pb-card .pb-card-img {
         background: #616160;
      }

      ::v-deep .pb-card .pb-card-content {
         color: #f0f0f0;
      }

      .bg-switcher .bg-switcher-label {
         color: #ccc;
      }
   }

   &.bg-dark {
      background-color: #333333;

      ::v-deep .pb-card .pb-card-img {
         background: #333333;
      }

      ::v-deep .pb-card .pb-card-content {
         color: #f0f0f0;
      }

      .bg-switcher .bg-switcher-label {
         color: #ccc;
      }
   }

   .farbkarte-color-item {
      border: 1px solid #eee;
      border-radius: 4px;
      min-height: 100px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
   }

   .farbkarte-color-img {
      max-height: 120px;
      object-fit: contain;
   }

   .farbkarte-color-number {
      font-size: 20px;
      font-weight: bold;
      padding: 20px 0;
   }
}
</style>
